import { Grid, Popper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import {
  DottedText,
  SvgCalendar,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import CustomDateMonthGrid from '../CustomDateMonthGrid';
import moment from 'moment';
import { MonthMap } from '../../../core-utils/helper-utils/EditDuration';
import CustomDateMonthSelector from '../CustomDateMonthSelector';
import {
  DATE_FORMAT,
  MONTH_YEAR_FORMAT,
} from '../../../core-utils/messages/CommonConstants';
import { useStores } from '../../../stores';
import componentConstants from '../../../core-utils/messages/componentConstants';
import type { CSSObject } from 'tss-react';
import { getMonthInResponseFormat } from '../../../core-utils/helper-utils/helper';

export interface InitialDates {
  start_month: string;
  end_month: string;
  plan_start_month: string;
  start_year: number;
  end_year: number;
  plan_end_month: string;
  recent_plan_year: Number;
}

export interface CurrentDates {
  startDate: string;
  endDate: string;
}

export interface NewEditDurationProps {
  startDateUseState: [string, React.Dispatch<React.SetStateAction<string>>];
  endDateUseState: [string, React.Dispatch<React.SetStateAction<string>>];
  getAllApis: any;
  isYearGrid?: boolean;
  showOnlyMainComponent?: boolean;
  forCreateNewReports?: boolean;
  hideTitle?: boolean;
  // styles related props
  mainComponentStyles?: CSSObject;
  infoStyles?: CSSObject;
  dateContainerStyles?: CSSObject;
  initialDates?: InitialDates;
  currentDates?: CurrentDates;
  showInfoText?: boolean;
  resetDates?: boolean;
}

const NewEditDuration: React.FC<NewEditDurationProps> = ({
  getAllApis,
  isYearGrid,
  startDateUseState,
  endDateUseState,
  showOnlyMainComponent,
  hideTitle,
  forCreateNewReports,
  //styles related props
  mainComponentStyles,
  infoStyles,
  dateContainerStyles,
  initialDates,
  currentDates,
  showInfoText = true,
  resetDates,
}) => {
  // states
  const [
    gridAnchorElement,
    setGridAnchorElement,
  ] = useState<null | HTMLElement>(null);
  const [
    startDateAnchorElement,
    setStartDateAnchorElement,
  ] = useState<null | HTMLElement>(null);
  const [
    endDateAnchorElement,
    setEndDateAnchorElement,
  ] = useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = startDateUseState;
  const [endDate, setEndDate] = endDateUseState;
  const [momentStartDate, setMomentStartDate] = useState<moment.Moment>();
  const [momentEndDate, setMomentEndDate] = useState<moment.Moment>();
  const [areDatesReady, setAreDatesReady] = useState<boolean>(false);
  const [startMonthYearString, setStartMonthYearString] = useState<string>('');
  const [endMonthYearString, setEndMonthYearString] = useState<string>('');
  const [planStartMonth, setPlanStartMonth] = useState<string>('');
  const [startYearArray, setStartYearArray] = useState<string[]>([]);
  const [endYearArray, setEndYearArray] = useState<string[]>([]);
  const [startYearIndex, setStartYearIndex] = useState<number>(0);
  const [endYearIndex, setEndYearIndex] = useState<number>(0);
  const [startMonthArray, setStartMonthArray] = useState<string[]>([]);
  const [endMonthArray, setEndMonthArray] = useState<string[]>([]);
  const [minPossibleDate, setMinPossibleDate] = useState<moment.Moment>();
  const [maxPossibleDate, setMaxPossibleDate] = useState<moment.Moment>();

  // constants
  const constants = componentConstants.MOLECULES.NEW_EDIT_DURATION;

  // stores
  const employerGroupStore = useStores().employerGroupStore;

  // styles
  const useStyles = makeStyles()({
    root: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: '160px',
      height: '27px',
      background: Boolean(gridAnchorElement)
        ? patientAccessTheme.palette.grey[50]
        : patientAccessTheme.palette.status.white,
      border: Boolean(gridAnchorElement)
        ? `1px solid ${patientAccessTheme.palette.primary[200]}`
        : `1px solid ${patientAccessTheme.palette.grey[300]}`,
      borderRadius: '4px',
      color: Boolean(gridAnchorElement)
        ? patientAccessTheme.palette.primary.main
        : patientAccessTheme.palette.text.highemphasis,
      padding: '0px',
      cursor: 'pointer',
    },
    paper: {
      zIndex: 1000,
      '& .MuiPopover-paper': {
        boxShadow: 'none',
      },
    },
    mainComponent: {
      boxSizing: 'border-box',
      width: '740px',
      height: '170px',
      background: patientAccessTheme.palette.status.white,
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      boxShadow: '0px 2px 24px 4px rgba(55, 70, 160, 0.08)',
      borderRadius: '4px',
      ...mainComponentStyles,
    },
    datesContainer: {
      marginLeft: '24px',
      ...dateContainerStyles,
    },
    dateTypographyItem: {
      marginRight: '12px',
    },
    infoItem: {
      boxSizing: 'border-box',
      width: '692px',
      height: '37px',
      background: '#F2F5FF',
      border: '1px solid #E5E9F0',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      marginLeft: '24px',
      marginBottom: '20px',
      ...infoStyles,
    },
    selectDatesField: {
      boxSizing: 'border-box',
      background: '#FFFFFF',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      borderRadius: '4px',
      height: '39px',
      width: '193px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginRight: '12px',
      cursor: 'pointer',
    },
  });

  const { classes } = useStyles();

  // functions
  const getMonthArray = (
    year: number,
    type: 'start' | 'end',
    currentDate?: moment.Moment,
    maxDate?: moment.Moment,
    minDate?: moment.Moment,
  ) => {
    const monthArray: string[] = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const localMaxPossibleDate = maxDate ?? maxPossibleDate;
    const localMinPossibleDate = minDate ?? minPossibleDate;
    const currentStartDate = currentDate ?? momentStartDate;
    const maxYear = localMaxPossibleDate?.year() ?? 0;
    const minYear = localMinPossibleDate?.year() ?? 5000;
    const currentYear = currentStartDate?.year();

    if (type === 'start') {
      if (year > minYear && year < maxYear) {
        return monthArray;
      } else if (year === minYear) {
        return monthArray.slice(localMinPossibleDate?.month() ?? 0);
      } else if (year === maxYear) {
        return monthArray.slice(0, (localMaxPossibleDate?.month() ?? 0) + 1);
      } else {
        return [];
      }
    } else {
      if (year === currentYear && year === maxYear) {
        return monthArray.slice(
          currentStartDate?.month(),
          (localMaxPossibleDate?.month() ?? 0) + 1,
        );
      } else if (year === currentYear && year !== maxYear) {
        return monthArray.slice(currentStartDate?.month() ?? 0);
      } else if (year !== currentYear && year !== maxYear) {
        return monthArray.slice(0, currentStartDate?.month() ?? 0);
      } else if (year !== currentYear && year === maxYear) {
        const minimumMonth = Math.min(
          localMaxPossibleDate?.month() ?? 0,
          currentStartDate?.month() ?? 0,
        );
        return monthArray.slice(0, minimumMonth);
      } else {
        return [];
      }
    }
  };

  const getDates = async () => {
    const dateFilterResponse =
      initialDates ?? employerGroupStore.employerGroupResponseData;

    const localMinPossibleDate = moment(
      MonthMap.get(dateFilterResponse.start_month)?.monthNumber +
        '-01-' +
        dateFilterResponse.start_year,
      'MM-DD-YYYY',
    );

    const localMaxPossibleDate = moment(
      MonthMap.get(dateFilterResponse.end_month)?.monthNumber +
        '-01-' +
        dateFilterResponse.end_year,
      'MM-DD-YYYY',
    ).endOf('month');

    setMinPossibleDate(localMinPossibleDate);
    setMaxPossibleDate(localMaxPossibleDate);

    const defaultStartDate = moment(localMaxPossibleDate, DATE_FORMAT)
      .subtract(11, 'months')
      .startOf('month');

    const localStartDate = defaultStartDate.isAfter(localMinPossibleDate)
      ? defaultStartDate
      : localMinPossibleDate;

    setEndDate(localMaxPossibleDate?.format(DATE_FORMAT) ?? '');
    setStartDate(localStartDate?.format(DATE_FORMAT) ?? '');
    setMomentEndDate(localMaxPossibleDate);
    setMomentStartDate(localStartDate);

    setStartMonthYearString(localStartDate?.format(MONTH_YEAR_FORMAT) ?? '');
    setEndMonthYearString(
      localMaxPossibleDate?.format(MONTH_YEAR_FORMAT) ?? '',
    );

    const localEndDate = localMaxPossibleDate?.isAfter(
      moment(localStartDate)?.add(11, 'months').endOf('month'),
    )
      ? moment(localStartDate)?.add(11, 'months').endOf('month')
      : localMaxPossibleDate;

    setStartMonthArray(
      getMonthArray(
        localStartDate?.year() ?? -1,
        'start',
        undefined,
        localMaxPossibleDate,
        localMinPossibleDate,
      ),
    );

    setEndMonthArray(
      getMonthArray(
        localEndDate?.year() ?? -1,
        'end',
        localStartDate,
        localMaxPossibleDate,
        localMinPossibleDate,
      ),
    );

    setPlanStartMonth(dateFilterResponse?.plan_start_month);

    // for start year array
    let startYearNumber = Number(dateFilterResponse?.start_year);
    const endYearNumber = Number(dateFilterResponse?.end_year);

    const startYearArray = [];
    while (startYearNumber <= endYearNumber) {
      startYearArray.push(startYearNumber.toString());
      startYearNumber++;
    }
    setStartYearArray([...startYearArray]);
    setStartYearIndex(
      startYearArray.indexOf(localStartDate?.year().toString() ?? ''),
    );

    // for end year array
    const endYearArray = [];
    endYearArray.push(localStartDate?.year().toString() ?? '');
    if (
      (localStartDate?.year() ?? 0) < (localMaxPossibleDate?.year() ?? 0) &&
      (localStartDate?.month() ?? 0) > 0
    ) {
      endYearArray.push(((localStartDate?.year() ?? 0) + 1).toString());
    }
    setEndYearArray([...endYearArray]);
    setEndYearIndex(
      endYearArray.indexOf(localEndDate?.year().toString() ?? ''),
    );

    if (currentDates) {
      const currentStartDate = moment(currentDates.startDate, DATE_FORMAT);
      const currentEndDate = moment(currentDates.endDate, DATE_FORMAT);
      if (currentStartDate.isValid() && currentEndDate.isValid()) {
        setPlanStartMonth(
          getMonthInResponseFormat((currentStartDate.month() + 1).toString()),
        );

        setMomentStartDate(currentStartDate);
        setStartDate(currentStartDate?.format(DATE_FORMAT) ?? '');

        setMomentEndDate(currentEndDate);
        setEndDate(currentEndDate?.format(DATE_FORMAT) ?? '');

        setStartMonthYearString(
          currentStartDate?.format(MONTH_YEAR_FORMAT) ?? '',
        );
        setEndMonthYearString(currentEndDate?.format(MONTH_YEAR_FORMAT) ?? '');

        setEndMonthArray(
          getMonthArray(currentEndDate?.year() ?? -1, 'end', currentStartDate),
        );

        // start year
        setStartYearIndex(
          startYearArray.indexOf(currentStartDate?.year().toString() ?? ''),
        );

        // for end year array
        const endYearArray = [];
        endYearArray.push(currentStartDate?.year().toString() ?? '');
        if (
          (currentStartDate?.year() ?? 0) <
            (localMaxPossibleDate?.year() ?? 0) &&
          (currentStartDate?.month() ?? 0) > 0
        ) {
          endYearArray.push(((currentStartDate?.year() ?? 0) + 1).toString());
        }
        setEndYearArray([...endYearArray]);
        setEndYearIndex(
          endYearArray.indexOf(currentEndDate?.year().toString() ?? ''),
        );
      }
    }

    setAreDatesReady((prev: any) => !prev);
  };

  const handleStartChange = (month: string) => {
    const localStartDate = moment(
      MonthMap.get(month)?.monthNumber +
        '-01-' +
        startYearArray[startYearIndex],
      'MM-DD-YYYY',
    );
    setPlanStartMonth(month);
    setMomentStartDate(localStartDate);
    setStartDate(localStartDate?.format(DATE_FORMAT) ?? '');

    const localEndDate = maxPossibleDate?.isAfter(
      moment(localStartDate)?.add(11, 'months').endOf('month'),
    )
      ? moment(localStartDate)?.add(11, 'months').endOf('month')
      : maxPossibleDate;

    setEndDate(localEndDate?.format(DATE_FORMAT) ?? '');
    setMomentEndDate(localEndDate);

    setStartMonthYearString(localStartDate?.format(MONTH_YEAR_FORMAT) ?? '');
    setEndMonthYearString(localEndDate?.format(MONTH_YEAR_FORMAT) ?? '');

    setEndMonthArray(
      getMonthArray(localEndDate?.year() ?? -1, 'end', localStartDate),
    );

    const endYearArray = [];
    endYearArray.push(localStartDate?.year().toString() ?? '');
    if (
      localStartDate?.year() < (maxPossibleDate?.year() ?? 0) &&
      (localStartDate?.month() ?? 0) > 0
    ) {
      endYearArray.push(((localStartDate?.year() ?? 0) + 1).toString());
    }
    setEndYearArray([...endYearArray]);
    setEndYearIndex(
      endYearArray.indexOf(localEndDate?.year().toString() ?? ''),
    );
  };

  const handleEndChange = (month: string) => {
    const localEndDate = moment(
      MonthMap.get(month)?.monthNumber + '-01-' + endYearArray[endYearIndex],
      'MM-DD-YYYY',
    );
    setEndDate(localEndDate?.endOf('month').format(DATE_FORMAT) ?? '');
    setMomentEndDate(localEndDate?.endOf('month'));
    setEndMonthYearString(localEndDate?.format(MONTH_YEAR_FORMAT) ?? '');
  };

  const handleSubmit = () => {
    setGridAnchorElement(null);
    setStartDateAnchorElement(null);
    setEndDateAnchorElement(null);
    handleAllApis();
  };

  const handleAllApis = () => {
    Boolean(startDate) && Boolean(endDate) ? getAllApis() : '';
  };

  const handleYearChange = (year: string) => {
    const currentDate = new Date(
      MonthMap.get(planStartMonth)?.monthNumber + '-01-' + year,
    );

    setStartDate(moment(currentDate).format(DATE_FORMAT));

    setStartMonthYearString(moment(currentDate).format(MONTH_YEAR_FORMAT));

    setEndDate(
      moment(currentDate).add(11, 'months').endOf('month').format(DATE_FORMAT),
    );

    setEndMonthYearString(
      moment(currentDate).add(11, 'months').format(MONTH_YEAR_FORMAT),
    );

    setStartDateAnchorElement(null);
  };

  const handleStartYearChange = (isIncrement: boolean, arrayLength: number) => {
    let monthArray: any = [];
    if (setStartYearIndex) {
      if (isIncrement) {
        setStartYearIndex((prev) => {
          if (prev < arrayLength - 1) {
            monthArray = getMonthArray(
              Number(startYearArray[prev + 1]),
              'start',
            );
            setStartMonthArray([...monthArray]);
            return prev + 1;
          }
          return prev;
        });
      } else {
        setStartYearIndex((prev) => {
          if (prev > 0) {
            monthArray = getMonthArray(
              Number(startYearArray[prev - 1]),
              'start',
            );
            setStartMonthArray([...monthArray]);
            return prev - 1;
          }
          return prev;
        });
      }
    }
  };

  const handleEndYearChange = (isIncrement: boolean, arrayLength: number) => {
    let monthArray: any = [];
    if (setEndYearIndex) {
      if (isIncrement) {
        setEndYearIndex((prev) => {
          if (prev < arrayLength - 1) {
            monthArray = getMonthArray(Number(endYearArray[prev + 1]), 'end');
            setEndMonthArray([...monthArray]);
            return prev + 1;
          }
          return prev;
        });
      } else {
        setEndYearIndex((prev) => {
          if (prev > 0) {
            monthArray = getMonthArray(Number(endYearArray[prev - 1]), 'end');
            setEndMonthArray([...monthArray]);
            return prev - 1;
          }
          return prev;
        });
      }
    }
  };

  const getMainComponent = () => {
    return (
      <Grid
        container
        className={classes.mainComponent}
        sx={{
          alignContent: 'flex-start',
        }}
      >
        {/* select start and end date */}
        {hideTitle ?? (
          <Grid
            container
            style={{
              margin: '18px 24px 12px 24px',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              fontSize="12px"
              lineHeight="22px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
            >
              {constants.TITLE}
            </Typography>
          </Grid>
        )}
        {/* info */}
        {showInfoText && (
          <Grid className={classes.infoItem}>
            <Info
              height={'16px'}
              width="16px"
              style={{ marginRight: '8px', marginLeft: '10px' }}
            />
            <Typography
              fontSize="12px"
              lineHeight="177%"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
            >
              {isYearGrid ? constants.YEARLY_INFO : constants.MONTHLY_INFO}
            </Typography>
          </Grid>
        )}
        <Grid
          container
          className={classes.datesContainer}
          sx={{
            alignItems: 'center',
          }}
        >
          {/* start date */}
          <Grid className={classes.dateTypographyItem}>
            <Typography
              fontSize="12px"
              lineHeight="22px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
            >
              {constants.START_DATE}
            </Typography>
          </Grid>
          <CustomDateMonthSelector
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              setStartDateAnchorElement(
                startDateAnchorElement ? null : event.currentTarget,
              );
            }}
            monthAndYear={startMonthYearString}
            styles={
              forCreateNewReports
                ? {
                    width: '234px',
                  }
                : {}
            }
          />
          <Popper
            open={Boolean(startDateAnchorElement)}
            anchorEl={startDateAnchorElement}
            placement="bottom-start"
            className={classes.paper}
          >
            <CustomDateMonthGrid
              currentMonth={planStartMonth}
              currentYear={
                isYearGrid
                  ? startMonthYearString?.slice(-4)
                  : startYearArray[startYearIndex]
              }
              yearArray={startYearArray}
              monthArray={startMonthArray}
              isYearGrid={isYearGrid}
              handleChange={isYearGrid ? handleYearChange : handleStartChange}
              handleYearChange={handleStartYearChange}
              currentDate={momentStartDate ?? moment()}
              yearIndex={startYearIndex}
              setAnchorElement={setStartDateAnchorElement}
            />
          </Popper>

          {/* end date */}
          <Grid className={classes.dateTypographyItem}>
            <Typography
              fontSize="12px"
              lineHeight="22px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
            >
              {constants.END_DATE}
            </Typography>
          </Grid>
          <CustomDateMonthSelector
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              setEndDateAnchorElement(
                endDateAnchorElement ? null : event.currentTarget,
              );
            }}
            monthAndYear={endMonthYearString}
            isDisabled={isYearGrid}
            styles={{
              width: forCreateNewReports ? '234px' : '193px',
              marginRight: '0px',
            }}
          />
          <Popper
            open={Boolean(endDateAnchorElement)}
            anchorEl={endDateAnchorElement}
            placement="bottom-start"
            className={classes.paper}
          >
            <CustomDateMonthGrid
              currentMonth={endMonthYearString?.slice(0, 3)}
              currentYear={
                isYearGrid
                  ? endMonthYearString?.slice(-4)
                  : endYearArray[endYearIndex]
              }
              yearArray={isYearGrid ? startYearArray : endYearArray}
              monthArray={endMonthArray}
              isYearGrid={isYearGrid}
              handleChange={handleEndChange}
              handleYearChange={handleEndYearChange}
              currentDate={momentEndDate ?? moment()}
              yearIndex={endYearIndex}
              setAnchorElement={setEndDateAnchorElement}
            />
          </Popper>

          {/* submite dates */}
          {!showOnlyMainComponent && (
            <Grid style={{ cursor: 'pointer' }} onClick={handleSubmit}>
              <DottedText text={constants.SUBMIT_DATES} />
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  // useEffect
  useEffect(() => {
    if (startMonthYearString === 'Invalid date' && initialDates) {
      getDates();
    }
    Boolean(momentStartDate) && Boolean(momentEndDate)
      ? getAllApis()
      : getDates();
  }, [
    areDatesReady,
    employerGroupStore?.employerGroupResponseData?.eg_nid,
    initialDates,
  ]);

  useEffect(() => {
    getDates();
  }, [resetDates]);

  return (
    <>
      {/* Main button */}
      {!showOnlyMainComponent && (
        <Grid
          container
          className={classes.root}
          component="button"
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            setGridAnchorElement(
              gridAnchorElement ? null : event.currentTarget,
            );
            if (gridAnchorElement) {
              setStartDateAnchorElement(null);
              setEndDateAnchorElement(null);
            }
          }}
        >
          <SvgCalendar
            height="16px"
            width="16px"
            color={
              Boolean(gridAnchorElement)
                ? patientAccessTheme.palette.primary.main
                : '#7C98B6'
            }
            style={{
              marginRight: '8px',
            }}
          />

          <Grid>
            <Typography
              fontSize="12px"
              lineHeight="20px"
              fontType="medium"
              margin="0px 4px 0px 0px"
            >
              {startMonthYearString?.slice(0, 3) +
                ' ' +
                startMonthYearString?.slice(-4) +
                ' -'}
            </Typography>
          </Grid>
          <Grid>
            <Typography fontSize="12px" lineHeight="20px" fontType="medium">
              {endMonthYearString?.slice(0, 3) +
                ' ' +
                endMonthYearString?.slice(-4)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {/* first Popper */}
      {showOnlyMainComponent ? (
        getMainComponent()
      ) : (
        <Popper
          open={Boolean(gridAnchorElement)}
          anchorEl={gridAnchorElement}
          placement="bottom-start"
          className={classes.paper}
        >
          {getMainComponent()}
        </Popper>
      )}
    </>
  );
};

export default NewEditDuration;
